import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { api } from '../api/client';
import { useAuthStore } from '../stores/authStore';
import { usePageTitle } from '../hooks/usePageTitle';

interface Post {
  id: string;
  author_name: string;
  author_role: string;
  channel: string;
  title: string;
  body: string;
  pinned: number;
  reply_count: number;
  created_at: string;
}

interface Reply {
  id: string;
  author_name: string;
  author_role: string;
  body: string;
  created_at: string;
}

interface PostDetail extends Post {
  replies: Reply[];
}

const CHANNELS = [
  { id: 'general', label: 'General', desc: 'Open to everyone' },
  { id: 'candidates', label: 'Candidates', desc: 'Candidate discussions' },
  { id: 'trainers', label: 'Trainers', desc: 'Trainer-only space' },
  { id: 'announcements', label: 'Announcements', desc: 'Official updates' },
];

const ROLE_BADGE: Record<string, string> = {
  candidate: 'bg-blue-100 text-blue-700',
  trainer: 'bg-green-100 text-green-700',
  admin: 'bg-purple-100 text-purple-700',
};

export function Community() {
  usePageTitle('Community');
  const user = useAuthStore((s) => s.user);
  const [channel, setChannel] = useState('general');
  const [posts, setPosts] = useState<Post[]>([]);
  const [selectedPost, setSelectedPost] = useState<PostDetail | null>(null);
  const [loading, setLoading] = useState(true);

  // New post form
  const [showNewPost, setShowNewPost] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');
  const [posting, setPosting] = useState(false);

  // Reply form
  const [replyBody, setReplyBody] = useState('');
  const [replying, setReplying] = useState(false);

  const visibleChannels = CHANNELS.filter((ch) => {
    if (user?.role === 'admin') return true;
    if (user?.role === 'trainer') return true;
    if (ch.id === 'trainers') return false;
    return true;
  });

  const fetchPosts = async (ch: string) => {
    setLoading(true);
    try {
      const data = await api.get<Post[]>(`/community/posts?channel=${ch}`);
      setPosts(data);
    } catch {
      setPosts([]);
    }
    setLoading(false);
  };

  useEffect(() => { fetchPosts(channel); }, [channel]);

  const handleCreatePost = async (e: React.FormEvent) => {
    e.preventDefault();
    setPosting(true);
    try {
      await api.post('/community/posts', { title: newTitle, body: newBody, channel });
      setShowNewPost(false);
      setNewTitle('');
      setNewBody('');
      fetchPosts(channel);
    } catch (err) {
      console.error(err);
    }
    setPosting(false);
  };

  const openPost = async (postId: string) => {
    try {
      const data = await api.get<PostDetail>(`/community/posts/${postId}`);
      setSelectedPost(data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPost) return;
    setReplying(true);
    try {
      await api.post(`/community/posts/${selectedPost.id}/replies`, { body: replyBody });
      setReplyBody('');
      openPost(selectedPost.id); // refresh
    } catch (err) {
      console.error(err);
    }
    setReplying(false);
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto mt-10 px-6 pb-20">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold text-mustard-green-dark">Community</h1>
            <p className="text-mustard-charcoal/60 mt-1">Connect with fellow candidates and trainers</p>
          </div>
          <button
            onClick={() => setShowNewPost(!showNewPost)}
            className="px-4 py-2 bg-mustard-green-dark text-white rounded-lg text-sm font-medium hover:bg-mustard-green transition-colors"
          >
            {showNewPost ? 'Cancel' : 'New Post'}
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Channel sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <h3 className="text-xs font-medium text-mustard-charcoal/50 uppercase tracking-wider mb-3">Channels</h3>
              <div className="space-y-1">
                {visibleChannels.map((ch) => (
                  <button
                    key={ch.id}
                    onClick={() => { setChannel(ch.id); setSelectedPost(null); }}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${channel === ch.id ? 'bg-mustard-green-dark text-white' : 'text-mustard-charcoal hover:bg-gray-100'}`}
                  >
                    <span className="font-medium">{ch.label}</span>
                    <p className={`text-xs mt-0.5 ${channel === ch.id ? 'text-white/60' : 'text-mustard-charcoal/40'}`}>{ch.desc}</p>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="lg:col-span-3">
            {/* New post form */}
            {showNewPost && (
              <form onSubmit={handleCreatePost} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-4">
                <input
                  type="text" value={newTitle} onChange={(e) => setNewTitle(e.target.value)}
                  placeholder="Post title" required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
                />
                <textarea
                  value={newBody} onChange={(e) => setNewBody(e.target.value)}
                  placeholder="Write your post..." required rows={4}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
                />
                <button type="submit" disabled={posting} className="px-6 py-2 bg-mustard-green-dark text-white text-sm rounded-lg font-medium disabled:opacity-50">
                  {posting ? 'Posting...' : 'Post'}
                </button>
              </form>
            )}

            {/* Selected post detail */}
            {selectedPost ? (
              <div>
                <button onClick={() => setSelectedPost(null)} className="text-sm text-mustard-green font-medium mb-4 hover:underline">
                  &larr; Back to posts
                </button>
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-medium text-sm text-mustard-charcoal">{selectedPost.author_name}</span>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_BADGE[selectedPost.author_role] || 'bg-gray-100'}`}>
                      {selectedPost.author_role}
                    </span>
                    <span className="text-xs text-mustard-charcoal/40">{new Date(selectedPost.created_at).toLocaleDateString('en-GB')}</span>
                  </div>
                  <h2 className="font-display text-xl font-bold text-mustard-green-dark mb-2">{selectedPost.title}</h2>
                  <p className="text-sm text-mustard-charcoal/70 whitespace-pre-wrap leading-relaxed">{selectedPost.body}</p>
                </div>

                {/* Replies */}
                <div className="space-y-3 mb-4">
                  {selectedPost.replies.map((reply) => (
                    <div key={reply.id} className="bg-white rounded-lg p-4 border border-gray-100">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-sm text-mustard-charcoal">{reply.author_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${ROLE_BADGE[reply.author_role] || 'bg-gray-100'}`}>
                          {reply.author_role}
                        </span>
                        <span className="text-xs text-mustard-charcoal/40">{new Date(reply.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                      <p className="text-sm text-mustard-charcoal/70">{reply.body}</p>
                    </div>
                  ))}
                </div>

                {/* Reply form */}
                <form onSubmit={handleReply} className="flex gap-2">
                  <input
                    type="text" value={replyBody} onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Write a reply..." required
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-mustard-green focus:border-transparent outline-none"
                  />
                  <button type="submit" disabled={replying} className="px-4 py-2 bg-mustard-green-dark text-white text-sm rounded-lg font-medium disabled:opacity-50">
                    Reply
                  </button>
                </form>
              </div>
            ) : (
              /* Post list */
              loading ? (
                <p className="text-mustard-charcoal/60">Loading...</p>
              ) : posts.length === 0 ? (
                <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-100 text-center">
                  <p className="text-mustard-charcoal/50">No posts in this channel yet. Be the first to start a conversation!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {posts.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => openPost(post.id)}
                      className="w-full text-left bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:border-mustard-green-mid transition-colors"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        {post.pinned ? <span className="text-xs bg-mustard-gold/20 text-mustard-gold px-2 py-0.5 rounded font-medium">Pinned</span> : null}
                        <span className="text-sm font-medium text-mustard-charcoal">{post.author_name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${ROLE_BADGE[post.author_role] || 'bg-gray-100'}`}>
                          {post.author_role}
                        </span>
                        <span className="text-xs text-mustard-charcoal/40 ml-auto">{new Date(post.created_at).toLocaleDateString('en-GB')}</span>
                      </div>
                      <h3 className="font-display font-bold text-mustard-green-dark mb-1">{post.title}</h3>
                      <p className="text-sm text-mustard-charcoal/50 line-clamp-2">{post.body}</p>
                      <p className="text-xs text-mustard-charcoal/40 mt-2">{post.reply_count} {post.reply_count === 1 ? 'reply' : 'replies'}</p>
                    </button>
                  ))}
                </div>
              )
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}
